import clsx from 'clsx'
import { Link } from 'wouter'
import { Logout01Icon, Settings02Icon } from '@hugeicons/core-solid-rounded'
import { HugeiconsIcon } from '@hugeicons/react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { GraphQL } from '@guestgoodie/graphql'
import * as Tooltip from '@radix-ui/react-tooltip'
import { Trans, useLingui } from '@lingui/react/macro'

import { popoverContainer, tooltipContainer } from '@/utils/popovers'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { Avatar } from './Avatar'
import { LanguageSwitcherMenuItem } from '@/features/i18n'

interface Props {
  user: GraphQL.User
  type: 'image_only' | 'row' | 'stacked'
  color?: 'text' | 'white'
  showTooltip?: boolean
}

function UserPreview(props: Props) {
  const { type, showTooltip = true, color = 'text', user } = props

  const fullName = `${user.givenName} ${user.lastName}`

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <div
          className={clsx(
            'flex flex-row p-2 gap-2 items-center',
            type === 'stacked' && 'flex-col',
            !showTooltip && 'pointer-events-none',
            color === 'text' && 'text-text',
            color === 'white' && 'text-white'
          )}
        >
          <div className="size-6 shrink-0">
            <Avatar url={user.avatarUrl} />
          </div>
          {type !== 'image_only' && (
            <h3 className="text-heading3 text-left">{fullName}</h3>
          )}
        </div>
      </Tooltip.Trigger>
      <Tooltip.Portal container={tooltipContainer}>
        <Tooltip.Content
          collisionPadding={20}
          className="flex flex-col gap-1 rounded-small bg-black/70 p-2 text-white z-tooltip"
          style={{
            maxWidth: 'var(--radix-tooltip-content-available-width)'
          }}
          sideOffset={5}
        >
          <div className="flex flex-row p-2 gap-2 items-center">
            <div className="size-8">
              <Avatar url={user.avatarUrl} />
            </div>
            <div className="flex flex-col gap-1">
              <h3 className="text-regular text-white">{fullName}</h3>
              <h3 className="text-small text-white">{user.email}</h3>
            </div>
          </div>
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  )
}

function Content(props: Props) {
  const itemClasses = clsx(
    'flex cursor-pointer flex-row items-center gap-2.5 rounded-small px-4 py-2 text-text text-regular outline-hidden focus:bg-primary-bright'
  )

  return (
    <div className="flex flex-col p-2 gap-1">
      <DropdownMenu.Item disabled>
        <UserPreview
          {...props}
          type="stacked"
          showTooltip={false}
          color="text"
        />
      </DropdownMenu.Item>
      <DropdownMenu.Item asChild>
        <Link to="/profile" className={itemClasses}>
          <HugeiconsIcon icon={Settings02Icon} className="size-4" />
          <Trans>Settings</Trans>
        </Link>
      </DropdownMenu.Item>
      <LanguageSwitcherMenuItem className={itemClasses} />
      <DropdownMenu.Item asChild>
        <a href="/api/auth/sign-out" className={itemClasses}>
          <HugeiconsIcon icon={Logout01Icon} className="size-4" />
          <Trans>Sign out</Trans>
        </a>
      </DropdownMenu.Item>
    </div>
  )
}

export function UserButton(props: Props) {
  const { t } = useLingui()

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={t`Open User Popover`}
        className="rounded-l"
      >
        <UserPreview {...props} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal container={popoverContainer}>
        <DropdownMenu.Content
          className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 flex flex-col gap-3 rounded-small bg-white p-3 shadow-md outline-hidden z-popover"
          sideOffset={5}
        >
          <Content {...props} />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

type CurrentProps = Omit<Props, 'user'>

export function CurrentUserButton(props: CurrentProps) {
  const { user } = useCurrentUser()

  if (!user) return null

  return <UserButton user={user} {...props} />
}
